import { PageBuilder } from "../lib/dom-json";
import template from './RecPlayer.html';

export default {
	template,
	data() {
		return {
			started: false,
			destroyed: false,
			inactive: false,

			scaleRatio: 1,
			fullscreen: false,
			frameWidth: 600,
			frameHeight: 400,
			pageUrl: '',

			isLoading: false,
			status: 'PLAYING',
			totalTime: null,
			currentTime: 0,
			firstFrameTime: null,
			lastLoadedFrameTime: null,
			isFastForwarding: false,

			events: [],
			loadedEvents: [],
			eventsToPlay: [],

			paddingTop: 0,
			mouseX: null,
			mouseY: null,
			docScrollX: 0,
			docScrollY: 0,
			fadePauseClass: ''
		};
	},
	props: ['domain', 'session-id', 'view-id'],
	computed: {
		videoWin() {
			return this.$refs.recordingFrame.contentWindow;
		},
		overlayStyle() {
			return {
				visibility: (this.started ? 'visible' : 'hidden'),
				width: this.frameWidth + 'px',
				height: this.frameHeight + 'px',
				transform: `scale(${this.scaleRatio})`,
				background: (this.inactive ? '#000' : 'none'),
				opacity: (this.inactive ? 0.9 : 1)
			};
		},
		frameStyle() {
			return {
				visibility: (this.started ? 'visible' : 'hidden'),
				width: this.frameWidth + 'px',
				height: this.frameHeight + 'px',
				transform: `scale(${this.scaleRatio})`
			};
		},
		mouseStyle() {
			let x = this.mouseX !== null ? this.mouseX : -100;
			let y = this.mouseY !== null ? this.mouseY : -100;
			return {
				left: x + 'px',
				top: y + 'px'
			};
		},
		recBoxStyle() {
			return {
				width: (this.frameWidth * this.scaleRatio) + 'px',
				height: (this.frameHeight * this.scaleRatio) + 'px'
			};
		},
		playingPercent() {
			if (!this.totalTime) return 0;

			let percent = this.currentTime / this.totalTime * 100;
			return Math.min(percent, this.loadedPercent, 100);
		},
		loadedPercent() {
			if (!this.lastLoadedFrameTime) return 0;

			let loadedTime = this.lastLoadedFrameTime - this.firstFrameTime;
			return Math.min(loadedTime / this.totalTime * 100, 100);
		}
	},
	methods: {
		async start() {
			let page = new PageBuilder(this.videoWin);
			let res = await this.http.get('Recording.getRecordingInfo', this.sessionId, this.viewId, this.app.query.rh);
			this.totalTime = res.duration;
			this.addMilestones(res.milestones);

			this.status = 'PLAYING';

			this.startLoading();
			this.startTime();
			this.startVideo(page);
		},
		addMilestones(milestones) {
			if (!milestones) return;

			milestones.forEach(e => {
				if (!e.time) return;

				let percent = e.time / this.totalTime * 100;

				let el = document.createElement('div');
				el.className = 'track-milestone track-milestone-' + e.event;
				el.style.left = percent + '%';
				this.$refs.milestones.appendChild(el);
			});
		},
		async startLoading() {
			let res, lastEvent, loadedEventsCnt = 0;
			do {
				if (this.destroyed) return;

				res = await this.http.get('Recording.getRecordingPortion', this.sessionId, this.viewId, loadedEventsCnt, this.app.query.rh);
				this.isLoading = false;
				this.started = true;
				if (!this.firstFrameTime) {
					this.firstFrameTime = res.events[0].time;
				}
				this.lastLoadedFrameTime = res.events[res.events.length - 1].time;

				loadedEventsCnt += res.events.length;
				lastEvent = res.events[res.events.length - 1];

				this.events = this.events.concat(res.events);
				this.loadedEvents = this.loadedEvents.concat(res.events);
			} while (lastEvent && lastEvent.event !== 'end');
		},
		startTime() {
			let lastTime = Date.now();
			setInterval(() => {
				if (this.destroyed) return;

				let now = Date.now();
				if (this.status !== 'PLAYING' || this.isLoading) {
					lastTime = now;
					return;
				}

				this.currentTime += now - lastTime;
				lastTime = now;
				if (!this.events.length) {
					this.isLoading = true;
					return;
				}

				while (this.events[0] && this.events[0].time - this.firstFrameTime <= this.currentTime) {
					let event = this.events.shift();
					this.eventsToPlay.push(event);
				}
			}, 10);
		},
		async startVideo(page) {
			while (true) {
				if (this.destroyed) return;

				let e = this.eventsToPlay.shift();

				if (!e) {
					await this.util.sleep(10);
					continue;
				}

				page.applyEvent(e.event, e.data);
				if (e.event === 'navigate') {
					this.pageUrl = e.data.url;
					this.setDocSize(e.data.width, e.data.height);
					this.calcScaleRatio();

					await this.util.sleep(10);

					this.performScroll({
						x: e.data.scrollX,
						y: e.data.scrollY
					});
				} else if (e.event === 'mousemove') {
					this.performMouseMove(e.data);
				} else if (e.event === 'click') {
					this.performClick(e.data);
				} else if (e.event === 'resize') {
					this.performResize(e.data);
				} else if (e.event === 'scroll') {
					this.performScroll(e.data);
				} else if (e.event === 'end') {
					this.status = 'ENDED';
					this.isLoading = false;
				} else if (e.event === 'state') {
					this.inactive = (e.data !== 'active');
				}
			}
		},
		performMouseMove(data) {
			this.mouseX = data.x;
			this.mouseY = data.y;
		},
		async performClick(data) {
			let cx = data.x + this.docScrollX;
			let cy = data.y + this.docScrollY;

			let wave = document.createElement('div');
			wave.className = 'click-wave';
			wave.style.left = cx + 'px';
			wave.style.top = cy + 'px';
			this.$refs.clickWaveBox.appendChild(wave);

			await this.util.sleep(100);
			wave.classList.add('click-anim');

			await this.util.sleep(700);
			this.$refs.clickWaveBox.removeChild(wave);
		},
		performResize(data) {
			this.setDocSize(data.width, data.height);
		},
		performScroll(data) {
			this.docScrollX = data.x || 0;
			this.docScrollY = data.y || 0;

			this.videoWin.scrollTo(this.docScrollX, this.docScrollY);
		},
		calcScaleRatio() {
			let playerHeight = this.fullscreen ? window.innerHeight : this.$el.parentNode.offsetHeight;

			let spaceWidth = this.$el.offsetWidth;
			let spaceHeight = playerHeight - this.$refs.top.offsetHeight - this.$refs.bottom.offsetHeight;

			let spaceRatio = spaceWidth / spaceHeight;
			let frameRatio = this.frameWidth / this.frameHeight;

			if (spaceRatio > frameRatio) {
				this.scaleRatio = spaceHeight / this.frameHeight;
				this.paddingTop = 0;
			} else {
				this.scaleRatio = spaceWidth / this.frameWidth;
				this.paddingTop = (spaceHeight - this.frameHeight * this.scaleRatio) / 2;
			}
		},
		setDocSize(width, height) {
			this.frameWidth = width;
			this.frameHeight = height;

			this.calcScaleRatio();
		},
		async onTrackClick(e) {
			this.isFastForwarding = true;

			let playerX = this.$el.getBoundingClientRect().left;
			let percent = (e.pageX - playerX) / e.currentTarget.offsetWidth;

			let curTime = this.currentTime;
			this.currentTime = percent * this.totalTime;
			if (this.currentTime < curTime) {
				this.resetProgress();
			}

			await this.util.sleep(1);
			this.isFastForwarding = false;
		},
		replay() {
			this.currentTime = 0;
			this.resetProgress();
			this.status = 'PLAYING';
		},
		resetProgress() {
			this.events = [...this.loadedEvents];
			this.docScrollX = 0;
			this.docScrollY = 0;
			this.mouseX = 0;
			this.mouseY = 0;
		},
		async statusClick() {
			if (this.status === 'ENDED') {
				this.replay();
				return;
			}
			if (this.status === 'PAUSED') {
				this.status = 'PLAYING';
				this.fadePauseClass = 'fa-play';
			} else {
				this.status = 'PAUSED';
				this.fadePauseClass = 'fa-pause';
			}
			await this.util.sleep(10);
			this.fadePauseClass += ' fade-anim';
			await this.util.sleep(500);
			this.fadePauseClass = '';
		},
		async toggleFullScreen() {
			this.fullscreen = !this.fullscreen;
			await this.util.sleep(10);
			this.calcScaleRatio();
		},
		async onKeyDown(e) {
			const KEY_ESC = 27;
			if (e.keyCode === KEY_ESC && this.fullscreen) {
				this.toggleFullScreen();
			}
		},
		msToTimer(ms) {
			let sec = Math.floor(ms / 1000);
			let min = Math.floor(sec / 60);
			sec = sec % 60;

			if (min < 10) {
				min = '0' + min;
			}
			if (sec < 10) {
				sec = '0' + sec;
			}
			return min + ':' + sec;
		},
	},
	mounted() {
		this.calcScaleRatio();
		this.start();
		window.addEventListener('resize', this.calcScaleRatio);
		document.addEventListener('keydown', this.onKeyDown)
	},
	destroyed() {
		this.destroyed = true;
	}
}